import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useMemo, useEffect } from "react";
import { CallApiRePassword, CallApiGetVerifyCode } from "../Api/Enforcement";
import { SwalSuccess } from "../Share/Swal";
import { accuracy, login, rePass } from "../Api/SubUrl";
import useCookieStorage from "./useCookieStorage";
import {
  FORGET_PASS_USER_ID_KEY,
  FORGET_PASS_PHONE_KEY,
} from "../Constants/localStorageKey";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { handleClearStoredAuth, notifyClientAuthChanged } from "../Share/Func/Storage";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

const getApiErrorMessage = (responseData) => {
  if (typeof responseData === "string") {
    return responseData;
  }

  return responseData?.Message ?? ERROR_MESSAGES.GENERIC_ERROR;
};

const mapRePasswordPayload = (formData) => ({
  AccountName: formData.accountName?.trim().toLowerCase() ?? "",
  OTP: formData.otp?.trim() ?? "",
  Password: formData.password ?? "",
  RePassword: formData.rePassword ?? "",
});

export default function useRePasswordPage() {
  const [loading, setLoading] = useState(false);
  const [isSentVerify, setIsSentVerify] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { getValue, removeValue } = useCookieStorage();
  const navigate = useNavigate();

  useEffect(() => {
    if (!getValue(FORGET_PASS_USER_ID_KEY) || !getValue(FORGET_PASS_PHONE_KEY)) {
      navigate(accuracy);
    }
  }, [getValue, navigate]);

  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("rePassword", rePass);

  const rePassSchemaForm = yup.object().shape({
    accountName: yup
      .string()
      .required("Vui lòng nhập tên tài khoản")
      .min(5, "Tên tài khoản phải có độ dài từ 5 đến 10 ký tự")
      .max(10, "Tên tài khoản phải có độ dài từ 5 đến 10 ký tự")
      .matches(
        "^[a-z0-9]*$",
        "Tên tài khoản chỉ được sử dụng các ký tự: a-z, 0-9"
      ),
    otp: yup.string().trim().required("Vui lòng nhập mã OTP"),
    password: yup
      .string()
      .required("Vui lòng nhập mật khẩu")
      .min(6, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .max(32, "Độ dài mật khẩu từ 6 đến 32 ký tự"),
    rePassword: yup
      .string()
      .required("Vui lòng xác nhận lại mật khẩu")
      .test(
        "is-same-password",
        "Mật khẩu không trùng khớp!",
        function (rePassword) {
          return rePassword === this.parent.password;
        }
      ),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        accountName: getValue(FORGET_PASS_USER_ID_KEY) || "",
        otp: "",
        password: "",
        rePassword: "",
      }),
      [getValue]
    ),
    resolver: yupResolver(rePassSchemaForm),
  });

  const handleRePass = useCallback(
    async (formData) => {
      if (loading) return;
      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        CallApiRePassword(token, mapRePasswordPayload(formData), (res) => {
          setLoading(false);
          if (res?.Status) {
            resetReCaptcha();
            const status = res.Status.status;
            const message = getApiErrorMessage(res.Status.data);

            if (status === 400) {
              ShowCustomErrorModal({
                message: `<p>${message.replace(/\n/g, "<br />")}</p>`,
                title: "Cập nhật thất bại",
              });
            } else if (status === 403) {
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                title: "Xác thực thất bại",
              });
            } else {
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.GENERIC_ERROR,
                title: "Đã xảy ra lỗi",
              });
            }
            return;
          }

          if (res !== "success") {
            resetReCaptcha();
            ShowCustomErrorModal({
              message: ERROR_MESSAGES.GENERIC_ERROR,
              title: "Đã xảy ra lỗi",
            });
            return;
          }

          reset();
          removeValue(FORGET_PASS_USER_ID_KEY);
          removeValue(FORGET_PASS_PHONE_KEY);
          SwalSuccess("Cập nhật thành công!");
          navigate(login);
          handleClearStoredAuth();
          notifyClientAuthChanged();
        });
      } catch (error) {
        setLoading(false);
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Cập nhật thất bại",
        });
      }
    },
    [getReCaptchaToken, loading, navigate, removeValue, reset, resetReCaptcha]
  );

  const handleGetVerifyCode = useCallback(() => {
    if (isSending) return;

    const accountName = getValue(FORGET_PASS_USER_ID_KEY);
    const phoneNumber = getValue(FORGET_PASS_PHONE_KEY);

    if (!accountName || !phoneNumber) {
      ShowCustomErrorModal({
        message: "<p>Vui lòng thực hiện lại bước quên mật khẩu!</p>",
        title: "Đã xảy ra lỗi",
      });
      navigate(accuracy);
      return;
    }

    setIsSending(true);

    const payload = {
      AccountName: accountName,
      PhoneNumber: phoneNumber,
    };

    CallApiGetVerifyCode(payload, (res) => {
      setIsSending(false);

      if (res?.Status) {
        const response = res.Status;
        const errorMessage =
          typeof response.data === "string"
            ? response.data
            : response.data?.Message ??
              "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ";

        ShowCustomErrorModal({
          message: `<p>${errorMessage}</p>`,
          title: response.status === 400 ? "Cập nhật thất bại" : "Đã xảy ra lỗi",
        });
        return;
      }

      if (!res?.success) {
        ShowCustomErrorModal({
          message:
            "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
          title: "Đã xảy ra lỗi",
        });
        return;
      }

      SwalSuccess("Mã xác nhận đã được gửi tới số điện thoại của bạn!");
      setIsSentVerify(true);
      setTimeout(() => setIsSentVerify(false), 10000);
    });
  }, [getValue, isSending, navigate]);

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Cập nhật thất bại",
    });
  }, []);

  return {
    control,
    handleSubmit: handleSubmit(handleRePass, handleInvalid),
    isSentVerify,
    handleGetVerifyCode,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
    loading,
    isSending,
  };
}
